import { useEffect, useMemo, useState } from "react";
import { Button } from "../../../components/buttons/button";
import { ProfileIcon } from "../../../components/cards/profilePic";
import Dropdown from "../../../components/dropdown/dropdown";
import { SearchInput } from "../../../components/inputs/searchInput";
import { Pill } from "../../../components/pill";
import { TableComponent } from "../../../components/table";
import { BgWrapper } from "../../../components/wrapper/bgWrapper";
import {
  MonthlyEventGuestList,
  MonthlyEventsSummary,
} from "../../../redux/features/admin/event/events.types";
import { getColorForTicket } from "../../../utils/constants";
import { formatToCurrency } from "../../../utils/helpers";
import { QueryParams } from "../../../utils/types";

export const MonthlyGuests = ({
  fetchingEventGuest,
  query,
  setQuery,
  eventsData,
  guestData,
  monthlyQuery,
  setMonthlyQuery,
}: {
  fetchingEventGuest: boolean;
  query: QueryParams;
  setQuery: (query: QueryParams) => void;
  eventsData: MonthlyEventsSummary;
  guestData: MonthlyEventGuestList;
  monthlyQuery: QueryParams;
  setMonthlyQuery: (monthlyQuery: QueryParams) => void;
}) => {
  const [selectedEvent, setSelectedEvent] = useState<any>(null);

  const filteredEvents = useMemo(() => {
    const list = eventsData?.data?.data || [];
    const term = (monthlyQuery?.search || "").toLowerCase();
    if (!term) return list;
    return list.filter((ev: any) => ev.name?.toLowerCase().includes(term));
  }, [eventsData, monthlyQuery.search]);

  useEffect(() => {
    if (!selectedEvent && filteredEvents.length > 0) {
      const first = filteredEvents[0];
      setSelectedEvent(first);
      setQuery({
        ...query,
        search: first.name,
        page: 1,
      });
    }
    // eslint-disable-next-line
  }, [filteredEvents, selectedEvent]);

  return (
    <BgWrapper className="mt-[2px] flex w-full flex-col gap-2.5 !rounded-t-none">
      <div className="flex flex-wrap items-center justify-between">
        <Dropdown
          className="order-last col-span-full md:order-first"
          position="bottom start"
          closeOnClick={false}
          customMenuItems={({ close }) => {
            return (
              <div className="flex w-80 flex-col rounded-xl border border-grey-200 bg-white">
                <div className="border-b-inherit-grey-200 border-b p-3">
                  <SearchInput
                    searchFunction={(value) => {
                      setMonthlyQuery({
                        ...monthlyQuery,
                        search: value || undefined,
                      });
                    }}
                    placeholder="Search"
                    className="md:!max-w-full"
                  />
                </div>
                <div className="max-h-60 overflow-y-auto">
                  {filteredEvents.length > 0 ? (
                    filteredEvents.map((event: any) => (
                      <div
                        onClick={() => {
                          setSelectedEvent(event);
                          setMonthlyQuery({
                            ...monthlyQuery,
                            search: undefined,
                          });
                          setQuery({
                            ...query,
                            search: event.name || undefined,
                            page: 1,
                          });
                          close();
                        }}
                        key={event._id}
                        className="flex w-full items-center gap-3 border-b border-b-grey-100 p-3 text-left last:border-b-0 hover:bg-grey-50"
                      >
                        <p className="truncate text-sm font-bold text-primary">
                          {event.name}
                        </p>
                      </div>
                    ))
                  ) : (
                    <div className="p-3 text-center text-grey-500">
                      No event found
                    </div>
                  )}
                </div>
              </div>
            );
          }}
        >
          <Button
            type="button"
            text={selectedEvent?.name}
            iconDimension={24}
            iconFirst={false}
            className="!max-w-fit !rounded-2xl !p-3 !text-sm"
            icon="chevron-down"
            variation="styled-grey"
          />
        </Dropdown>
        <p className="text-base font-bold text-primary">
          {guestData?.data?.totalCount ?? 0} Guest
          {(guestData?.data?.totalCount || 0) > 1 && "s"}
        </p>
      </div>
      <TableComponent
        headers={[]}
        rows={
          guestData?.data?.data?.map((user, idx) => {
            const usedColors: string[] = [];

            return {
              id: idx,
              items: [
                <div
                  className={`flex w-full flex-1 flex-col justify-between gap-2 md:flex-row md:items-center`}
                >
                  <div className="flex items-center gap-[5px]">
                    <ProfileIcon dimension={33} className="!p-2" />
                    <div className="flex items-center gap-[2px]">
                      <p className="text-sm font-bold text-grey-900">
                        {user?.name} ∙
                      </p>
                      <p className="text-sm text-grey-600">
                        {user?.totalAmountPaid > 0
                          ? `(${formatToCurrency(user?.totalAmountPaid, {
                              currency: "NGN",
                            })}) Paid`
                          : "FREE"}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {user.ticketsBought?.map((t, index) => {
                      const colorPair = getColorForTicket(t.name, usedColors);
                      usedColors.push(colorPair);
                      const [textColor, bgColor] = colorPair.split(",");

                      return (
                        <Pill
                          key={`${index}`}
                          text={`${t.quantity}X ${t.name}`}
                          variation="green"
                          className="!line-clamp-1 !w-full !min-w-0 !max-w-fit !overflow-hidden !text-left"
                          style={{
                            color: textColor,
                            backgroundColor: bgColor,
                          }}
                        />
                      );
                    })}
                  </div>
                </div>,
              ],
            };
          }) ?? []
        }
        loadingRows={query.limit}
        isLoading={fetchingEventGuest}
        isRowClickable={false}
        isTableEmpty={[0, undefined].includes(guestData?.data?.data?.length)}
        emptyTitle="No Guest Yet"
        emptySubTitle="Guests breakdown would appear when the event is published"
        emptyImage="/assets/images/paper-plane-glass.svg"
        isPaginated
        currentPage={query.page ?? 1}
        totalCount={guestData?.data?.totalCount ?? 0}
        itemsPerPage={query.limit!}
        pageRangeDisplayed={3}
        onPageChange={(page) => {
          setQuery({
            ...query,
            page: page,
          });
        }}
        className=""
      />
    </BgWrapper>
  );
};
